import React, { useMemo, useState } from "react";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { Button } from "@/components/ui/button";
import { Settings } from "lucide-react";
import { CreateGroupChatModal } from "./CreateGroupChatModal";
import { DeleteChannelButton } from "./DeleteChannelButton";
import { useTranslation } from "react-i18next";
import ChannelUserAvatar from "./ChannelUserAvatar";
import { PinnedMessagesDialog } from "./PinnedMessagesDialog";

export const InternalChannelHeader = () => {
  const { t } = useTranslation();
  const { client } = useChatContext();
  const { channel, watcher_count } = useChannelStateContext();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const chatType = channel.data?.chat_type;
  const isGroup =
    chatType === "group" ||
    (!chatType &&
      ((channel.data?.member_count ?? 0) > 2 || !!channel.data?.name));
  const otherMember = Object.values(channel.state.members).find(
    (member) => member.user?.id !== client.userID,
  )?.user;
  const channelName = isGroup
    ? (channel.data?.name as string) || t("chats.groupChat")
    : otherMember?.name ||
      (channel.data?.name as string) ||
      t("chats.internalChats");

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };
  const handleEditModalClose = () => setIsEditModalOpen(false);

  const initialData = useMemo(
    () => ({
      id: channel.id!,
      name: (channel.data?.name as string) || "",
      targetUserIds: Object.keys(channel.state.members),
    }),
    [channel.id, channel?.data?.name, channel.state.members],
  );

  return (
    <>
      <div className="flex h-[62px] w-full shrink-0 items-center justify-between gap-4 border-b border-neutral-200 bg-white px-4">
        <div className="flex min-w-0 items-center gap-4">
          <ChannelUserAvatar
            name={channelName}
            isGroup={isGroup}
            className="size-9"
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-eathy-comfort-1 truncate text-base font-semibold">
              {channelName}
            </span>
            <span className="truncate text-xs text-neutral-500">
              {channel.data?.member_count} {t("chats.members")}
              {!!watcher_count && (
                <>
                  , {watcher_count} {t("chats.online")}
                </>
              )}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <PinnedMessagesDialog />
          {isGroup ? (
            <>
              <DeleteChannelButton
                channelId={channel.id!}
                channelName={(channel.data?.name as string) || ""}
              />
              <Button
                type="button"
                variant="new_ghost"
                size="icon"
                className="rounded-lg text-zinc-600"
                onClick={handleEditClick}
                title={t("chats.edit")}
                aria-label={t("chats.editGroupChat")}
              >
                <Settings />
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {isGroup ? (
        <CreateGroupChatModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          initialData={initialData}
        />
      ) : null}
    </>
  );
};
